import { followUpEventPath } from '../follow-up/constants';
import { connectPath } from '../member-in-event/constants';
import type { ConnectClosureReason } from '../member-in-event/types';

import type { CalendarEventInput } from './types';

/**
 * Copy and pure functions for the registered state (`MEM-017`).
 *
 * Everything testable in this slice lives here, because there is no DOM renderer in this
 * repository and `apps/web`'s test script matches `.test.ts` only. A behaviour left inside a
 * component is a behaviour with no test, so the calendar entry — the part with a real trap in it —
 * is a function over data rather than an event handler.
 */

/*
 * `REGISTERED_HEADING` — *"You're going!"* — was here, at the head of the confirmed panel. Sumit
 * asked for that row to go while reviewing #528: the 10 September header card says it in the
 * status pill (`YOURE_GOING_STATUS`, below) and carries the ticket, the calendar and cancelling,
 * so the page was saying one thing twice. `MEM-017` AC-1 is reworded to the card in this pull
 * request. The heading has no second home and is not kept as a spare.
 */

/** The order line, on the past-event panel and the receipt. `#` prefixes the code as the prototype does. */
export function confirmedLine(confirmationCode: string): string {
  return `Confirmed · order #${confirmationCode}`;
}

/*
 * `REGISTER_ACTION_LABEL` was here — 'Register — free', on a full-width button in the page body.
 *
 * `MEM-020` AC-1 makes the reserve action *"a pinned action bar at the foot of the event detail
 * page reading 'Reserve my spot · $25'"*, and AC-6 gives the free wording as
 * *"Reserve my spot · Free"*. Both live in `event-orders/constants.ts`'s `reserveActionLabel`,
 * which is one function precisely because the two differ by one word. Keeping this constant as
 * well would have left a second label for one control, on a screen where a member can only usefully
 * be offered one.
 */
export const CANCEL_ACTION_LABEL = 'Cancel my registration';
export const CALENDAR_ACTION_LABEL = 'Calendar';

/**
 * `MEM-013` AC-6 — *"the cancellation confirmation says so"*.
 *
 * A member cancelling loses their meet list and their matches for that event, and the story's own
 * note is that they *"may not expect [it], and which is a reason not to cancel casually"*. The
 * prototype writes it as **"You'll lose your spot at {event}, along with your matches and meet
 * list for this event."** — this is that sentence, with the event named.
 *
 * It is a *consequence* rather than a rule, which is exactly why it has to be said out loud here:
 * nothing else in the flow mentions it, and a member discovering it afterwards has already lost
 * the plan they made.
 *
 * **What it deliberately does not promise.** Not "your meet list will be deleted" — it is not.
 * `MEM-013` AC-6 removes *access*, and the entries survive on the row, so re-registering brings
 * back the list. Saying "deleted" would be a false statement that made cancelling feel more
 * final than it is.
 */
export function cancellationConsequence(eventName: string): string {
  return `You'll lose your spot at ${eventName}, along with your matches and your meet list for this event. Registering again brings your meet list back.`;
}

/**
 * The confirmation dialog's description: the consequence, then what happens to the money
 * (`MEM-013` AC-6; `MEM-023` AC-8).
 *
 * A join of two **served** sentences and nothing else. The money sentence is the server's
 * `CancellationPreview` — what comes back and what the service fee does, off the order's stamped
 * amounts — or the terms' own refusal where nothing comes back; a free place has
 * no preview and the description is the consequence alone. No figure is composed here and no
 * sentence is written here, so the dialog cannot describe the deal differently from the record.
 *
 * The money sentence comes **second** so the loss a member may not expect (`MEM-013` AC-6) is
 * still read first; both sit in reading order before the dialog's confirm control.
 */
export function whatCancellingMeans(
  consequence: string,
  preview: { readonly kind: 'money-back'; readonly sentence: string } | { readonly kind: 'nothing-back'; readonly message: string } | null,
): string {
  if (preview === null) return consequence;
  return `${consequence} ${preview.kind === 'money-back' ? preview.sentence : preview.message}`;
}

export const CANCEL_CONFIRM_TITLE = 'Cancel your registration?';
export const CANCEL_CONFIRM_ACTION_LABEL = 'Yes, cancel my spot';
export const CANCEL_KEEP_LABEL = 'Keep my spot';

/*
 * `MATCHES_ACTION_LABEL` and `TICKET_ACTION_LABEL` were here — the confirmed card's row of pills,
 * and then the past-event panel's. Both rows are gone (Sumit, reviewing #528), and the header card
 * carries what they reached: the ticket behind *View your ticket*, the matches behind the
 * stepper's *Before* part. Neither label has a second home, so neither is kept as a spare.
 */

/*
 * `SIGN_IN_TO_REGISTER` was here — the grey bar a signed-out visitor saw in the event page's
 * registration region. It is gone (TW 44206362): the prototype has no such bar, and the pinned
 * *Reserve my spot* already takes a visitor into sign-up (`MEM-058`).
 */

/**
 * `MEM-017` AC-2: a calendar entry carrying the event name, the start and end instants **with
 * timezone**, and the venue name.
 *
 * ## The trap, and why this emits UTC
 *
 * The failure AC-2 is written against is a calendar entry generated from naive local times: the
 * event moves by however many hours separate the member's device from the venue, and they find
 * out when their phone reminds them at the wrong time. `MEM-015` AC-2 is the same trap one layer
 * in.
 *
 * So every instant here is emitted as `YYYYMMDDTHHMMSSZ` — a UTC instant, which is a timestamp
 * *with* a timezone and is the one form no calendar client can misread. The alternative, a
 * floating local time plus a `VTIMEZONE` block naming `America/New_York`, is more faithful to how
 * the organizer thinks about the event and strictly more ways to be wrong: it requires shipping
 * correct transition rules for every zone, and a client that ignores or mis-parses the block
 * silently reinterprets the time as the reader's own.
 *
 * The event's own timezone is still carried, in the description, because it is what the member
 * needs if they are travelling — but nothing depends on parsing it.
 *
 * Returns `null` when the event has no start instant. A calendar entry for an event with no date
 * is not a degraded entry, it is a wrong one.
 */
export function calendarEntry(input: CalendarEventInput): string | null {
  if (input.startsAt === null) return null;
  const start = toIcsInstant(input.startsAt);
  if (start === null) return null;

  // No end instant: default to two hours, which is what a calendar does with an open-ended entry
  // anyway, and say so in the description rather than claiming a precise finish.
  const endSource =
    input.endsAt ?? new Date(Date.parse(input.startsAt) + 2 * 60 * 60 * 1000).toISOString();
  const end = toIcsInstant(endSource);
  if (end === null) return null;

  const description = [
    `Confirmation: ${input.confirmationCode}`,
    input.timezone === null ? null : `Event timezone: ${input.timezone}`,
    input.endsAt === null ? 'End time not set by the organizer; two hours assumed.' : null,
  ]
    .filter((line): line is string => line !== null)
    .join('\\n');

  const lines = [
    'BEGIN:VCALENDAR',
    'VERSION:2.0',
    'PRODID:-//H2HSpark//Member App//EN',
    'CALSCALE:GREGORIAN',
    'BEGIN:VEVENT',
    `UID:${input.confirmationCode}@h2hspark`,
    `DTSTAMP:${start}`,
    `DTSTART:${start}`,
    `DTEND:${end}`,
    `SUMMARY:${escapeIcsText(input.name)}`,
    input.venueName === null ? null : `LOCATION:${escapeIcsText(input.venueName)}`,
    `DESCRIPTION:${escapeIcsText(description)}`,
    'END:VEVENT',
    'END:VCALENDAR',
  ].filter((line): line is string => line !== null);

  // CRLF, because RFC 5545 says so and at least one desktop client refuses a file without it.
  return `${lines.join('\r\n')}\r\n`;
}

/** A UTC ISO instant as iCalendar spells one: `20260917T120000Z`. */
function toIcsInstant(iso: string): string | null {
  const parsed = Date.parse(iso);
  if (Number.isNaN(parsed)) return null;
  return `${new Date(parsed).toISOString().replace(/[-:]/g, '').replace(/\.\d{3}/, '')}`;
}

/** RFC 5545 §3.3.11: backslash, semicolon and comma are escaped; newlines are already `\n`. */
function escapeIcsText(value: string): string {
  return value.replace(/\\/g, '\\\\').replace(/;/g, '\\;').replace(/,/g, '\\,');
}

/** The filename a member sees in their downloads. */
export function calendarFileName(eventName: string): string {
  const slug = eventName
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');
  return `${slug.length > 0 ? slug : 'event'}.ics`;
}

// ─── after the evening (`MEM-015` AC-7) ───────────────────────────────────────────────────────

/**
 * What the confirmed panel becomes once the event is over (`MEM-015` AC-7).
 *
 * The header card's pill says *You're going* until the evening is over, and on the following
 * morning that would be the page telling a member something that has already happened as though
 * it had not. This is the panel in the past tense: the place was held, the evening happened, and
 * what is offered below are the three things that outlived it.
 *
 * It deliberately does **not** say the member attended. Whether they came through the door is the
 * door's record (`EVT-030`) and the attended list's, and a panel that assumed it would be the one
 * screen claiming attendance for somebody who bought a ticket and stayed home.
 */
export const EVENT_ENDED_HEADING = 'This event has ended';

/**
 * **What replaced the controls that closed** (`MEM-031` AC-7).
 *
 * AC-7 is *"at event end the connect code, the scanner, the open-to-connect toggle and the
 * in-event roster all close, and the connect token is invalidated"*, and `MEM-031`'s own note gives
 * the physical reason: *"the code was on a screen in a public room"*, so a photograph taken during
 * the evening has to be worthless by the time the room empties.
 *
 * The server already enforces every word of that — `connectWindowState` closes at `endsAt` exactly
 * and the token stops resolving — so this sentence is not a rule being applied a second time. It
 * is the panel saying *why* the Connect control is gone rather than leaving a member to conclude
 * the product lost their evening: the scanning closed, and the people are where they went.
 *
 * A sentence rather than a disabled Connect pill, which is `MEM-031` AC-1's own instruction one
 * surface over: *"a disabled scanner on a laptop is worse than no scanner — it implies the feature
 * should work here and does not."*
 */
export const CONNECT_CLOSED_AFTER_EVENT =
  'Connecting closed when the event ended. The people you logged are in your connections, and your follow-up is below.';

/*
 * `FOLLOW_UP_ACTION_LABEL` was here, on the past-event panel's third pill. The follow-up is now the
 * stepper's *After* part, which on a past event reads `FOLLOW_UP_NOW_LABEL` — the prototype's own
 * call-to-action wording for exactly that case.
 */

// ─── the event header card and its stepper (`MEM-053`) ─────────────────────────────────────────

/** The header card's link on the event page — `Web App.dc.html` line 268, verbatim. */
export const VIEW_TICKET_LINK = 'View your ticket';

// ─── the per-event pages' shared frame (`MEM-053` AC-1) ────────────────────────────────────────

/**
 * The back link at the head of a per-event page — the prototype's own *All events* (§2.4 item 1,
 * §2.7 item 1), which in its navigation is Your Events.
 */
export const ALL_EVENTS_BACK_LABEL = 'All events';

/**
 * The lock card's action, for a member who reached a per-event page without a place.
 *
 * No price on it, unlike `reserveActionLabel`'s pinned bar: this card does not hold the event's
 * ticket types, and a second reserve control quoting a different figure from the one on the event
 * page is how two prices for one evening start to exist. It is a link **to** the event page, where
 * the real action is.
 */
export const RESERVE_MY_SPOT_LABEL = 'Reserve my spot';

/**
 * What each per-event page says to a member who is not registered — the prototype's lock cards
 * (§2.4 item 3 and §2.6 item 2), verbatim, with one sentence of ours where it had none.
 *
 * The matches sentence in the prototype writes the release interval into the copy (*"48 hours
 * before the event"*). It is configuration (`MEM-MAT-002`, and `MEM-024` AC-2 makes it settable per
 * event), so the interval is the one phrase not taken verbatim — the same edit `PHASE_NOTES` makes
 * for the same reason.
 */
export const PER_EVENT_LOCKED = {
  attendees: {
    title: "Who's going opens once you reserve",
    description: 'The full list of who is coming is for people going. Reserve your spot to see it.',
  },
  matches: {
    title: 'Your matches unlock when you reserve',
    description:
      'We match you with a few people worth meeting shortly before the event — reserve your spot to see who.',
  },
  connect: {
    title: "Connect opens once you're going",
    description:
      'The tap-to-connect code and your connection log are available to members attending this event.',
  },
  requests: {
    title: 'Connection requests open when you reserve',
    description:
      'People you meet at an event can ask to connect. Reserve your spot and any requests from this event arrive here.',
  },
  followUp: {
    title: 'Follow-up is for members who came',
    description:
      'Openers are written from the people you logged at an event. Reserve your spot, and your follow-up appears here the day after.',
  },
} as const;

/**
 * The header card's link on every **other** page about this event — `Web App.dc.html` §3.4,
 * verbatim: *"View your ticket" (detail) or "View full event details" (others)*.
 */
export const EVENT_DETAILS_LINK = 'View full event details';

/** The header card's Requests control — `Web App.dc.html` §3.6. */
export const REQUESTS_CONTROL_LABEL = 'Requests';

/**
 * **This event's connection requests** (`MEM-053` AC-6, `MEM-032` AC-6).
 *
 * The prototype's header card opens a per-event requests page, and the control's count is already
 * this event's — so the destination has to be too, or a member holding a badge reading *2* lands on
 * a list of six and the number they tapped is wrong where they arrive. The **global** requests
 * screen at `/app/connections/requests` is unchanged and is still what Connections opens
 * (`MEM-032` AC-6's *"reached from the connections list"*); this is a second door onto the same
 * queue, filtered to one evening.
 */
export function eventPageRequestsPath(eventId: string): string {
  return `/app/events/${encodeURIComponent(eventId)}/requests`;
}

/** The accessible name of the Requests control, which carries only a digit on the screen. */
export function requestsControlName(count: number): string {
  return count === 1
    ? '1 connection request from this event'
    : `${String(count)} connection requests from this event`;
}

/**
 * **What the member's own record says about this event** — the header card's status pill
 * (`MEM-053` AC-4).
 *
 * The prototype's rule is *"Attended" if the event is past* — for **every** past event, booked or
 * not (`handoff-corrections.md`). Attendance is a check-in, not the passage of time
 * (`MEM-EVT-001`), and the member's registration record does not carry one. What does is the
 * follow-up summary, which is generated only for a member the door checked in (`MEM-035` AC-1). So:
 *
 *   - before the event ends, *You're going*;
 *   - after it, *Attended* only once that summary exists;
 *   - after it with no summary — never scanned (`MEM-EVT-012`), or scanned and the summary not due
 *     yet — *Registered*, which is true in both cases and claims nothing.
 */
export type RegisteredStatus =
  | { readonly tone: 'going'; readonly label: string }
  | { readonly tone: 'attended'; readonly label: string }
  | { readonly tone: 'neutral'; readonly label: string };

export const YOURE_GOING_STATUS = "You're going";
export const ATTENDED_STATUS = 'Attended';
export const REGISTERED_STATUS = 'Registered';

export function registeredStatus(input: {
  readonly hasEnded: boolean;
  readonly followUp: FollowUpFact;
}): RegisteredStatus {
  if (!input.hasEnded) return { tone: 'going', label: YOURE_GOING_STATUS };
  if (input.followUp.kind === 'ready') return { tone: 'attended', label: ATTENDED_STATUS };
  return { tone: 'neutral', label: REGISTERED_STATUS };
}

/** What the member's match set for this event is, as far as the page knows. */
export type MatchesFact =
  | { readonly kind: 'released'; readonly count: number }
  | { readonly kind: 'not-released' }
  | { readonly kind: 'unknown' };

/** What the Connect surface answered — its own closure vocabulary, from the server. */
export type ConnectFact =
  | { readonly kind: 'open' }
  | { readonly kind: 'closed'; readonly reason: ConnectClosureReason }
  | { readonly kind: 'unknown' };

/** Whether the member's follow-up summary for this event exists yet (`MEM-035`). */
export type FollowUpFact =
  | { readonly kind: 'ready' }
  | { readonly kind: 'none' }
  | { readonly kind: 'unknown' };

/**
 * **What the After part says once the evening is over** — the prototype's own label for it.
 *
 * `Web App.dc.html`'s `phases` carries a third variant, `cta`, hard-wired off (`const cta =
 * false`) and naming in the same line the case it was drawn for: on a past event the After part
 * reads *"Follow up now"*, with a brand border and the gradient disc. That is exactly the state a
 * past event's page is in — the evening is over, Connect has closed, the matches are a snapshot,
 * and the follow-up is the only thing left to do — so the variant is switched on there.
 */
export const FOLLOW_UP_NOW_LABEL = 'Follow up now';

/** The stepper's three parts, in the prototype's words (§3.3). */
export const EVENT_PHASES = [
  { key: 'before', tag: 'Before', label: 'Matches' },
  { key: 'during', tag: 'During', label: 'Connect' },
  { key: 'after', tag: 'After', label: 'Follow-up' },
] as const;

/**
 * What a part that is not open says instead (`MEM-053` AC-3) — *when* it opens, in a stepper
 * cell's few words. The long sentences are the destination screens' own; these are the same facts
 * at the size of a label.
 *
 * *Shortly before the event* rather than *48h* for the reason `your-events/constants.ts` gives:
 * the release interval is configuration (`MEM-MAT-002`), not a promise the member app can make.
 */
export const PHASE_NOTES = {
  matchesBefore: 'Opens shortly before the event',
  matchesNone: 'None were released for this event',
  connectAtCheckIn: 'Opens when you’re checked in',
  connectNotCheckedIn: 'Ask the event team to check you in',
  connectEnded: 'Closed when the event ended',
  followUpAfter: 'Opens after the event',
  followUpAttendees: 'For members checked in at the event',
} as const;

const CONNECT_NOTES: Readonly<Record<ConnectClosureReason, string>> = {
  'not-registered': PHASE_NOTES.connectAtCheckIn,
  'no-schedule': PHASE_NOTES.connectAtCheckIn,
  'doors-not-open': PHASE_NOTES.connectAtCheckIn,
  'not-checked-in': PHASE_NOTES.connectNotCheckedIn,
  'event-over': PHASE_NOTES.connectEnded,
};

/**
 * **The Before · During · After stepper, decided** (`MEM-053` AC-2, AC-3).
 *
 * A part opens only when its surface exists for this member, each by the rule that already owns
 * it — never a new one:
 *
 *   - **Matches** once the set is released (`MEM-024`): the matching route answers `null` until
 *     then, for an unreleased set, an opted-out member and a set that does not exist alike.
 *   - **Connect** while they are checked in and the event has not ended (`MEM-028` AC-7,
 *     `MEM-031` AC-7). The server's own closure reason decides it, so the stepper cannot open a
 *     surface the server would refuse. After the event it stays a cell with a sentence in it —
 *     `MEM-015` AC-7's *"absent rather than disabled"* is about a control, and a static
 *     explanation is the sentence that criterion puts in its place.
 *   - **Follow-up** once the summary exists (`MEM-035`).
 *
 * `unknown` (still loading, or a read that failed) is a closed cell with no note: a cell that
 * opened and then shut under somebody's thumb is worse than one that opens a moment late.
 *
 * `active` names the page the member is on; the event page passes none (`MEM-053` AC-2).
 */
export function eventPhaseParts(input: {
  readonly eventId: string;
  readonly hasEnded: boolean;
  readonly matches: MatchesFact;
  readonly connect: ConnectFact;
  readonly followUp: FollowUpFact;
  readonly active?: 'before' | 'during' | 'after' | undefined;
}): ReadonlyArray<{
  readonly key: 'before' | 'during' | 'after';
  readonly tag: string;
  readonly label: string;
  readonly state: 'active' | 'open' | 'cta' | 'locked';
  readonly href: string;
  readonly note: string | null;
}> {
  const id = encodeURIComponent(input.eventId);
  const open = {
    before: input.matches.kind === 'released',
    during: !input.hasEnded && input.connect.kind === 'open',
    after: input.followUp.kind === 'ready',
  };
  const notes = {
    before:
      input.matches.kind === 'unknown'
        ? null
        : input.hasEnded
          ? PHASE_NOTES.matchesNone
          : PHASE_NOTES.matchesBefore,
    during: input.hasEnded
      ? PHASE_NOTES.connectEnded
      : input.connect.kind === 'closed'
        ? CONNECT_NOTES[input.connect.reason]
        : null,
    after:
      input.followUp.kind === 'unknown' && input.hasEnded
        ? null
        : input.hasEnded
          ? PHASE_NOTES.followUpAttendees
          : PHASE_NOTES.followUpAfter,
  };
  // Each destination through the function that owns it, so the stepper cannot come to point at a
  // path the screen that owns it has moved (`MEM-047` pins two of the three, because mailed links
  // land on them).
  const hrefs = {
    before: `/app/events/${id}/matches`,
    during: connectPath(input.eventId),
    after: followUpEventPath(input.eventId),
  };
  /**
   * The one part that is **the thing to do next**: Follow-up, on a past event, once the summary
   * exists. Nothing else on that page is live — Connect closed at the end (`MEM-031` AC-7) and the
   * matches are a snapshot to read (`MEM-026`) — and Sumit asked for the follow-up to stay the
   * obvious step when the past-event panel lost its buttons (2026-09-11).
   */
  const callToAction = input.hasEnded && open.after && input.active !== 'after' ? 'after' : null;
  return EVENT_PHASES.map((phase) => ({
    key: phase.key,
    tag: phase.tag,
    label: phase.key === callToAction ? FOLLOW_UP_NOW_LABEL : phase.label,
    state:
      input.active === phase.key
        ? 'active'
        : phase.key === callToAction
          ? 'cta'
          : open[phase.key]
            ? 'open'
            : 'locked',
    href: hrefs[phase.key],
    note: open[phase.key] ? null : notes[phase.key],
  }));
}

/**
 * **The match alert** — `Web App.dc.html` §2.2, item 3: *"We found 3 matches for you"*, with the
 * **real** count, only once the set is released and has somebody in it, and only before the
 * event ends (the prototype's `showMatchAlert`, less its fixed *3* — `handoff-corrections.md`).
 * `null` means no alert.
 */
export function matchAlertTitle(input: {
  readonly hasEnded: boolean;
  readonly matches: MatchesFact;
}): string | null {
  if (input.hasEnded || input.matches.kind !== 'released' || input.matches.count === 0) return null;
  return input.matches.count === 1
    ? 'We found 1 match for you'
    : `We found ${String(input.matches.count)} matches for you`;
}

export const MATCH_ALERT_DESCRIPTION = 'People worth meeting at this event, with a reason why.';
export const MATCH_ALERT_ACTION = 'See your matches';
